import { useEffect, useRef, type ReactElement, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { AlertCircle, CheckSquare, FolderOpen, Loader2, RotateCcw } from "lucide-react";
import type { AdminMediaResponse } from "@ckgo2/shared/types";
import { uiLocale } from "@/i18n";
import {
  shouldLoadMore,
  type MediaGridState,
  type MediaLibrary,
  type mediaGridState,
} from "@/hooks/useMediaLibrary";
import MediaTile, { type TileClickIntent } from "./MediaTile";

/**
 * Mřížka stránky Média: stavy načítání, chyby a prázdna, hlavička s celkovým počtem
 * a „Vybrat vše načtené“, dlaždice a sentinel donačítání posunem (vize podnety-tymu-2026-10,
 * F7.5).
 *
 * `MediaGridView` je čistá (bez hooků, `t` v props), aby šla v testu zavolat jako funkce;
 * proto tlačítka retry, zrušení filtrů a výběru načtených stojí přímo v jejím JSX, ne ve
 * vnořené komponentě. Stav (`state`, `nextPageError`) mřížka nepočítá, dostane ho od
 * `MediaPage` z `mediaGridState`, ze kterého stránka počítá i „Zrušit filtry“ v liště.
 */

const GRID_CLASS =
  "grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6";
const SKELETON_TILES = 12;

export interface MediaGridViewProps {
  state: MediaGridState;
  items: readonly AdminMediaResponse[];
  /** `total` z API pro tytéž filtry (null, dokud nejsou data). */
  total: number | null;
  selectedIds: ReadonlySet<string>;
  trashMode: boolean;
  restoringId: string | null;
  hasActiveFilters: boolean;
  isFetchingNextPage: boolean;
  nextPageError: boolean;
  sentinelRef: RefObject<HTMLDivElement | null>;
  t: TFunction;
  onIntent: (id: string, intent: TileClickIntent) => void;
  onRestore: (id: string) => void;
  onRetry: () => void;
  onClearFilters: () => void;
  onSelectLoaded: (ids: string[]) => void;
}

export function MediaGridView({
  state,
  items,
  total,
  selectedIds,
  trashMode,
  restoringId,
  hasActiveFilters,
  isFetchingNextPage,
  nextPageError,
  sentinelRef,
  t,
  onIntent,
  onRestore,
  onRetry,
  onClearFilters,
  onSelectLoaded,
}: MediaGridViewProps): ReactElement {
  if (state === "loading") {
    return (
      <div data-media-skeleton="" aria-busy="true" className={GRID_CLASS}>
        {Array.from({ length: SKELETON_TILES }, (_, i) => (
          <div
            key={i}
            className="animate-pulse overflow-hidden rounded-lg border-2 border-transparent"
          >
            <div className="aspect-square bg-gray-200 dark:bg-gray-800" />
            <div className="space-y-1 p-1.5">
              <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-800" />
              <div className="h-2.5 w-1/2 rounded bg-gray-100 dark:bg-gray-800/60" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (state === "error") {
    return (
      <div
        data-media-error=""
        role="alert"
        className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
      >
        <AlertCircle className="h-8 w-8" />
        <p className="text-sm font-medium">{t("media.page.loadErrorShort")}</p>
        <button
          type="button"
          data-media-retry=""
          onClick={onRetry}
          className="btn-secondary text-sm"
        >
          <RotateCcw className="h-4 w-4" /> {t("common.actions.retry")}
        </button>
      </div>
    );
  }

  if (state === "empty") {
    const text = trashMode
      ? t("media.page.trashEmpty")
      : hasActiveFilters
        ? t("media.page.emptyFiltered")
        : t("media.page.noMedia");
    return (
      <div
        data-media-empty=""
        className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-300 text-gray-500 dark:border-gray-700"
      >
        <FolderOpen className="h-14 w-14 text-gray-300" />
        <p className="text-lg font-medium">{text}</p>
        {hasActiveFilters && (
          <button
            type="button"
            data-media-clear-filters=""
            onClick={onClearFilters}
            className="btn-secondary text-sm"
          >
            {t("media.page.clearFilters")}
          </button>
        )}
      </div>
    );
  }

  const shownTotal = total ?? items.length;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p
          data-media-total=""
          data-count={shownTotal}
          className="text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {t("media.page.total", {
            count: shownTotal,
            formatted: new Intl.NumberFormat(uiLocale()).format(shownTotal),
          })}
        </p>
        <button
          type="button"
          data-media-select-loaded=""
          onClick={() => onSelectLoaded(items.map((m) => m.id))}
          className="btn-ghost text-sm"
        >
          <CheckSquare className="h-4 w-4" /> {t("media.page.selectLoaded")}
        </button>
      </div>

      <div className={GRID_CLASS}>
        {items.map((m) => (
          <MediaTile
            key={m.id}
            item={m}
            selected={selectedIds.has(m.id)}
            trashMode={trashMode}
            restoring={restoringId === m.id}
            t={t}
            onIntent={onIntent}
            onRestore={onRestore}
          />
        ))}
      </div>

      <div ref={sentinelRef} aria-hidden="true" className="h-px" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <Loader2 className="text-primary-500 h-6 w-6 animate-spin" />
        </div>
      )}
      {nextPageError && (
        <div
          data-media-error=""
          role="alert"
          className="flex items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>{t("media.page.loadErrorShort")}</span>
          <button
            type="button"
            data-media-retry=""
            onClick={onRetry}
            className="btn-secondary text-sm"
          >
            <RotateCcw className="h-4 w-4" /> {t("common.actions.retry")}
          </button>
        </div>
      )}
    </div>
  );
}

/** Data seznamu, která mřížka čte (výsledek `useMediaLibrary`). */
export type MediaGridLibrary = Pick<
  MediaLibrary,
  | "items"
  | "total"
  | "hasNextPage"
  | "isFetching"
  | "isFetchingNextPage"
  | "fetchNextPage"
  | "refetch"
>;

export type MediaGridProps = Omit<
  MediaGridViewProps,
  | "t"
  | "sentinelRef"
  | "onRetry"
  | "state"
  | "nextPageError"
  | "items"
  | "total"
  | "isFetchingNextPage"
> & {
  library: MediaGridLibrary;
  /** Stav z `mediaGridState`, týž, ze kterého stránka počítá „Zrušit filtry“ v liště. */
  grid: ReturnType<typeof mediaGridState>;
};

/**
 * Kontejner mřížky: překlady a donačítání posunem. Posouvacím kontejnerem adminu je
 * `<main className="flex-1 overflow-auto">` (`AdminLayout`), ne okno; s kořenem výhledu by
 * `rootMargin` nic nepředčítal, proto kořen = nejbližší `<main>`.
 *
 * `IntersectionObserver` hlásí jen ZMĚNU průniku: když po donačtení (nebo obnově) sentinel
 * zůstane ve výhledu, nový záznam nepřijde a donačítání by se zastavilo před `total`.
 * Pozorovatel se proto vytvoří znovu po každé změně počtu položek a stavu načítání
 * (`observe` doručí výchozí stav).
 */
export default function MediaGrid({ library, grid, ...view }: MediaGridProps): ReactElement {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { items, total, hasNextPage, isFetching, isFetchingNextPage, fetchNextPage, refetch } =
    library;
  const { state, nextPageError } = grid;
  const itemsLength = items.length;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (state !== "items" || !sentinel || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries.some((e) => e.isIntersecting);
        if (
          shouldLoadMore({
            isIntersecting,
            hasNextPage,
            isFetching,
            isFetchNextPageError: nextPageError,
          })
        ) {
          void fetchNextPage();
        }
      },
      { root: sentinel.closest("main") ?? null, rootMargin: "800px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [state, itemsLength, hasNextPage, isFetching, nextPageError, fetchNextPage]);

  // Po chybě další stránky `fetchNextPage` (smaže chybu a donačítání se obnoví), jinak `refetch`.
  const onRetry = () => void (nextPageError ? fetchNextPage() : refetch());

  return (
    <MediaGridView
      {...view}
      state={state}
      nextPageError={nextPageError}
      items={items}
      total={total}
      isFetchingNextPage={isFetchingNextPage}
      t={t}
      sentinelRef={sentinelRef}
      onRetry={onRetry}
    />
  );
}
